import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  EnvelopeSimple, CheckCircle, WarningCircle, SignOut, Copy, Eye, EyeSlash, Trash, Lock, ShieldCheck, ShieldWarning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function StatusPill({ ok, okLabel = "Active", offLabel = "Not set" }) {
  return (
    <span
      data-testid={`auth-status-${ok ? "active" : "inactive"}`}
      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${
        ok ? "bg-success/10 text-success" : "bg-secondary text-muted-foreground"
      }`}
    >
      {ok ? <CheckCircle size={13} weight="fill" /> : <WarningCircle size={13} />}
      {ok ? okLabel : offLabel}
    </span>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { hid } = useHousehold();
  const [gmail, setGmail] = useState({ connected: false, configured: false, email: null });
  const [settings, setSettings] = useState(null);
  const [routingRules, setRoutingRules] = useState([]);

  // google
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [savingGoogle, setSavingGoogle] = useState(false);
  // password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // 2FA
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, recovery_codes_remaining: 0 });
  const [twoFAStep, setTwoFAStep] = useState("idle"); // idle | setup | confirm | recovery_codes | disable
  const [twoFAQR, setTwoFAQR] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFARecoveryCodes, setTwoFARecoveryCodes] = useState([]);
  const [twoFABusy, setTwoFABusy] = useState(false);

  const loadAll = () => {
    api.get("/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/auth/2fa/status").then((r) => setTwoFAStatus(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (!hid) return;
    api.get("/ingest/routing-rules", { params: { household_id: hid } })
      .then((r) => setRoutingRules(r.data?.rules || []))
      .catch(() => setRoutingRules([]));
  }, [hid]);

  const deleteRoutingRule = async (payorKey) => {
    try {
      await api.delete(`/ingest/routing-rules/${payorKey}`, { params: { household_id: hid } });
      setRoutingRules((current) => current.filter((rule) => rule.payor_key !== payorKey));
      toast.success("Routing rule removed");
    } catch (_e) {
      toast.error("Failed to remove routing rule");
    }
  };

  const saveGoogle = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { toast.error("Enter both Client ID and Client Secret"); return; }
    setSavingGoogle(true);
    try {
      const r = await api.put("/settings", { google_client_id: clientId.trim(), google_client_secret: clientSecret.trim() });
      setSettings(r.data); setClientId(""); setClientSecret("");
      toast.success("Google OAuth credentials saved");
      loadAll();
    } catch (e) { toast.error("Failed to save credentials"); }
    finally { setSavingGoogle(false); }
  };

  const clearGoogle = async () => {
    if (!window.confirm("Remove Google credentials and disconnect Gmail?")) return;
    const r = await api.delete("/settings/google");
    setSettings(r.data);
    loadAll();
    toast.success("Google credentials removed");
  };

  const connect = async () => {
    try {
      const res = await api.get("/gmail/connect");
      window.location.href = res.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Add Google credentials first"); }
  };

  const disconnect = async () => {
    await api.post("/gmail/disconnect");
    toast.success("Gmail disconnected");
    loadAll();
  };

  const copyRedirect = () => {
    if (settings?.redirect_uri) {
      navigator.clipboard.writeText(settings.redirect_uri);
      toast.success("Redirect URI copied");
    }
  };

  const hasPassword = !!settings?.has_password;

  // ── 2FA handlers ──────────────────────────────────────────────────────────
  const start2FASetup = async () => {
    setTwoFABusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setTwoFAQR(r.data.qr);
      setTwoFAStep("confirm");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start 2FA setup"); }
    finally { setTwoFABusy(false); }
  };

  const confirm2FA = async () => {
    if (!twoFACode.trim()) { toast.error("Enter the 6-digit code"); return; }
    setTwoFABusy(true);
    try {
      const r = await api.post("/auth/2fa/enable", { code: twoFACode.trim() });
      setTwoFARecoveryCodes(r.data.recovery_codes);
      setTwoFAStep("recovery_codes");
      setTwoFACode("");
      setTwoFAStatus({ enabled: true, recovery_codes_remaining: 8 });
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setTwoFABusy(false); }
  };

  const disable2FA = async () => {
    if (!twoFACode.trim()) { toast.error("Enter your authenticator code to confirm"); return; }
    setTwoFABusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: twoFACode.trim() });
      setTwoFAStatus({ enabled: false, recovery_codes_remaining: 0 });
      setTwoFAStep("idle");
      setTwoFACode("");
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setTwoFABusy(false); }
  };

  const changePassword = async () => {
    if (hasPassword && !currentPw) { toast.error("Enter your current password"); return; }
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: hasPassword ? currentPw : undefined,
        new_password: newPw,
      });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      toast.success(hasPassword ? "Password updated" : "Password set");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update password");
    } finally { setSavingPw(false); }
  };

  const sectionTitle = "flex items-center gap-2.5 flex-1 min-w-0";

  return (
    <div className="space-y-8 animate-fade-up max-w-3xl">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Account</p>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Settings</h1>
      </div>

      {/* Profile */}
      <div className="bg-card border border-border rounded-sm p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">Profile</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display text-xl font-bold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <Button data-testid="settings-logout-button" variant="outline" onClick={logout} className="rounded-sm font-semibold">
            <SignOut size={16} className="mr-1" /> Sign out
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-6" data-testid="settings-routing-rules">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Ingest</p>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">Document routing rules</h2>
          </div>
        </div>
        {routingRules.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No document routing rules yet. Confirm a few matching documents in Ingest and automatic filing rules will appear here.
          </p>
        ) : (
          <div className="space-y-3">
            {routingRules.map((rule) => (
              <div
                key={rule.payor_key}
                data-testid={`routing-rule-${rule.payor_key}`}
                className="flex items-start justify-between gap-4 rounded-sm border border-border bg-secondary/30 p-4"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{rule.payor_label || rule.payor_key}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {rule.target_asset_name || "No destination selected"} · {rule.confirm_count} confirm{rule.confirm_count === 1 ? "" : "s"}
                    {rule.auto_file ? " · auto" : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm shrink-0"
                  data-testid={`routing-rule-delete-${rule.payor_key}`}
                  onClick={() => deleteRoutingRule(rule.payor_key)}
                >
                  <Trash size={16} className="mr-1" /> Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Authorizations — single collapsible section */}
      <div className="bg-card border border-border rounded-sm" data-testid="authorizations-section">
        <div className="flex items-center gap-2 p-6 pb-2">
          <ShieldCheck size={20} weight="fill" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Authorizations</p>
        </div>
        <p className="text-sm text-muted-foreground px-6 pb-2">
          Manage every credential and authorization for your account in one place. Click a row to expand it.
        </p>

        <Accordion type="multiple" className="px-6 pb-2">
          {/* Google / Gmail */}
          <AccordionItem value="google" data-testid="auth-item-google">
            <AccordionTrigger data-testid="auth-trigger-google" className="hover:no-underline">
              <span className={sectionTitle}>
                <EnvelopeSimple size={18} weight="fill" className="text-accent" />
                <span className="font-semibold">Google Authorization</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">Gmail ingestion</span>
              </span>
              <span className="mr-3">
                <StatusPill
                  ok={!!gmail.connected}
                  okLabel="Connected"
                  offLabel={settings?.google_configured ? "Not connected" : "Not set"}
                />
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-muted-foreground mb-4">
                Connect Gmail so we can scan for emailed statements with PDF attachments. Create OAuth credentials in the{" "}
                <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline font-semibold">Google Cloud Console</a>{" "}
                (enable the Gmail API, then create a Web OAuth client).
              </p>

              <div className="bg-secondary rounded-sm p-3 mb-4">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Authorized redirect URI (add this in Google Console)</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono flex-1 truncate">{settings?.redirect_uri}</code>
                  <button data-testid="copy-redirect-button" onClick={copyRedirect} className="text-muted-foreground hover:text-foreground"><Copy size={16} /></button>
                </div>
              </div>

              {settings?.google_configured ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-success/5 border border-success/20 rounded-sm p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={20} weight="fill" className="text-success" />
                      <div>
                        <p className="font-semibold text-sm">Credentials saved</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Client ID {settings.google_client_id_masked} {settings.google_from_env && "(from server env)"}
                        </p>
                      </div>
                    </div>
                    {!settings.google_from_env && (
                      <Button data-testid="clear-google-button" variant="outline" onClick={clearGoogle} className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground">
                        <Trash size={16} className="mr-1" /> Remove
                      </Button>
                    )}
                  </div>

                  {gmail.connected ? (
                    <div className="flex items-center justify-between bg-card border border-border rounded-sm p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={20} weight="fill" className="text-success" />
                        <div>
                          <p className="font-semibold text-sm">Gmail connected</p>
                          <p className="text-xs text-muted-foreground">{gmail.email}</p>
                        </div>
                      </div>
                      <Button data-testid="disconnect-gmail-button" variant="outline" onClick={disconnect} className="rounded-sm">Disconnect</Button>
                    </div>
                  ) : (
                    <Button data-testid="connect-gmail-button" onClick={connect} className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]">
                      <EnvelopeSimple size={16} className="mr-1" /> Connect Gmail
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Client ID</Label>
                    <Input data-testid="google-client-id-input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" className="rounded-sm mt-1.5 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Client Secret</Label>
                    <Input data-testid="google-client-secret-input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-..." className="rounded-sm mt-1.5 font-mono" />
                  </div>
                  <Button data-testid="save-google-button" onClick={saveGoogle} disabled={savingGoogle} className="rounded-sm font-semibold">
                    {savingGoogle ? "Saving…" : "Save Credentials"}
                  </Button>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Password & Security */}
          <AccordionItem value="password" data-testid="auth-item-password" className="border-b-0">
            <AccordionTrigger data-testid="auth-trigger-password" className="hover:no-underline">
              <span className={sectionTitle}>
                <Lock size={18} weight="fill" className="text-accent" />
                <span className="font-semibold">Password &amp; Security</span>
              </span>
              <span className="mr-3"><StatusPill ok={hasPassword} okLabel="Set" offLabel="Not set" /></span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-muted-foreground mb-4">
                {hasPassword
                  ? "Update the password you use to sign in. You'll need your current password to confirm the change."
                  : "You currently sign in with Google. Set a password to also enable email + password sign-in."}
              </p>
              <div className="space-y-3">
                {hasPassword && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Current password</Label>
                    <Input
                      data-testid="current-password-input"
                      type={showPw ? "text" : "password"}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="••••••••"
                      className="rounded-sm mt-1.5"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">New password</Label>
                  <Input
                    data-testid="new-password-input"
                    type={showPw ? "text" : "password"}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="At least 8 characters"
                    className="rounded-sm mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Confirm new password</Label>
                  <Input
                    data-testid="confirm-password-input"
                    type={showPw ? "text" : "password"}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter new password"
                    className="rounded-sm mt-1.5"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                    <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} data-testid="show-password-toggle" />
                    Show passwords
                  </label>
                  <Button data-testid="change-password-button" onClick={changePassword} disabled={savingPw} className="rounded-sm font-semibold">
                    {savingPw ? "Saving…" : hasPassword ? "Update Password" : "Set Password"}
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Two-Factor Authentication */}
          <AccordionItem value="2fa" data-testid="auth-item-2fa" className="border-b-0">
            <AccordionTrigger data-testid="auth-trigger-2fa" className="hover:no-underline">
              <span className={sectionTitle}>
                <ShieldWarning size={18} weight="fill" className="text-accent" />
                <span className="font-semibold">Two-Factor Authentication</span>
              </span>
              <span className="mr-3">
                <StatusPill ok={twoFAStatus.enabled} okLabel="Enabled" offLabel="Disabled" />
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {twoFAStatus.enabled ? (
                // ── Enabled state ──────────────────────────────────────────
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Your account is protected with an authenticator app.
                    {twoFAStatus.recovery_codes_remaining > 0
                      ? ` You have ${twoFAStatus.recovery_codes_remaining} recovery code${twoFAStatus.recovery_codes_remaining !== 1 ? "s" : ""} remaining.`
                      : " You have no recovery codes remaining — consider disabling and re-enabling to generate new ones."}
                  </p>
                  {twoFAStep === "disable" ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Enter your current authenticator code to confirm:</p>
                      <Input
                        data-testid="2fa-disable-code-input"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\s/g, ""))}
                        placeholder="000000"
                        maxLength={8}
                        className="rounded-sm font-mono text-center tracking-[0.3em] w-40"
                        inputMode="numeric"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button data-testid="2fa-disable-confirm-button" onClick={disable2FA} disabled={twoFABusy} variant="destructive" className="rounded-sm font-semibold text-xs">
                          {twoFABusy ? "Disabling…" : "Disable 2FA"}
                        </Button>
                        <Button variant="outline" className="rounded-sm text-xs font-semibold" onClick={() => { setTwoFAStep("idle"); setTwoFACode(""); }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button data-testid="2fa-disable-button" variant="outline" onClick={() => setTwoFAStep("disable")} className="rounded-sm font-semibold text-xs">
                      Disable 2FA
                    </Button>
                  )}
                </div>
              ) : twoFAStep === "confirm" ? (
                // ── QR code + code entry ───────────────────────────────────
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Scan this QR code with Google Authenticator, Authy, or any TOTP app, then enter the 6-digit code to confirm.</p>
                  {twoFAQR && (
                    <div className="inline-block border border-border rounded-sm p-2 bg-white">
                      <img src={`data:image/png;base64,${twoFAQR}`} alt="2FA QR Code" className="w-44 h-44" data-testid="2fa-qr-code" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-bold">Verification code</Label>
                    <Input
                      data-testid="2fa-setup-code-input"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      maxLength={6}
                      inputMode="numeric"
                      className="rounded-sm font-mono text-center text-xl tracking-[0.4em] w-40 mt-1.5"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="2fa-enable-confirm-button" onClick={confirm2FA} disabled={twoFABusy || twoFACode.length < 6} className="rounded-sm font-semibold text-xs">
                      {twoFABusy ? "Verifying…" : "Verify & Enable"}
                    </Button>
                    <Button variant="outline" className="rounded-sm text-xs font-semibold" onClick={() => { setTwoFAStep("idle"); setTwoFACode(""); setTwoFAQR(null); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : twoFAStep === "recovery_codes" ? (
                // ── Recovery codes shown once ──────────────────────────────
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-sm bg-warning/10 border border-warning/30">
                    <ShieldWarning size={18} weight="fill" className="text-warning shrink-0" />
                    <p className="text-sm text-warning font-medium">Save these codes — they will only be shown once. Each code can be used once if you lose your device.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {twoFARecoveryCodes.map((code) => (
                      <code key={code} className="text-sm font-mono bg-secondary px-3 py-1.5 rounded-sm tracking-wider text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-sm text-xs font-semibold"
                    onClick={() => { navigator.clipboard.writeText(twoFARecoveryCodes.join("\n")); toast.success("Copied to clipboard"); }}
                  >
                    <Copy size={14} className="mr-1" /> Copy all
                  </Button>
                  <div />
                  <Button data-testid="2fa-done-button" className="rounded-sm font-semibold text-xs" onClick={() => { setTwoFAStep("idle"); setTwoFARecoveryCodes([]); }}>
                    Done
                  </Button>
                </div>
              ) : (
                // ── Disabled / idle state ──────────────────────────────────
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Add a second layer of security. After entering your password, you'll be asked for a 6-digit code from your authenticator app.
                  </p>
                  <Button data-testid="2fa-enable-button" onClick={start2FASetup} disabled={twoFABusy} className="rounded-sm font-semibold text-xs">
                    {twoFABusy ? "Loading…" : "Enable 2FA"}
                  </Button>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="h-2" />
      </div>
    </div>
  );
}
